import { Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem, TextField, IconButton, Button } from '@mui/material';
import { Refresh, Clear } from '@mui/icons-material';
import { DatePicker } from 'antd';
import { Pagination } from 'antd';
import Loading from 'components/Loading';
import { isEmpty } from 'lodash';
import React, { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import FeedbackTableRow from './FeedbackTableRow';
import SentryWrap from 'components/Sentry';

const { RangePicker } = DatePicker;

const tableHeader = [
  { id: 1, headerText: 'Sequence No.' },
  { id: 2, headerText: 'Name' },
  { id: 3, headerText: 'Username' },
  { id: 4, headerText: 'Role' },
  { id: 5, headerText: 'Date' },
  { id: 6, headerText: 'Content Preview' },
  { id: 7, headerText: 'Attachments' },
  { id: 8, headerText: 'Status' },
  { id: 9, headerText: 'Notes' },
];

const FeedbackModuleComponent = ({ feedbackData, loading, onUpdateItem, onRefresh, totalCount = 0 }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSortOrder, setDateSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
    pageSizeOptions: ['30', '50', '100'],
  });

  // Check if any filter has been changed from default
  const hasActiveFilters = useMemo(() => {
    return (
      statusFilter !== 'all' ||
      dateFilter !== 'all' ||
      (dateFilter === 'custom' && customDateRange !== null) ||
      searchTerm.trim() !== ''
    );
  }, [statusFilter, dateFilter, customDateRange, searchTerm]);

  const getDateRange = () => {
    const now = moment();
    let startDate = '';
    let endDate = '';
    
    if (dateFilter === 'today') {
      startDate = now.startOf('day').valueOf().toString();
      endDate = now.endOf('day').valueOf().toString();
    } else if (dateFilter === 'week') {
      startDate = now.clone().subtract(7, 'days').startOf('day').valueOf().toString();
      endDate = now.endOf('day').valueOf().toString();
    } else if (dateFilter === 'custom' && customDateRange && customDateRange.length === 2) {
      startDate = customDateRange[0].startOf('day').valueOf().toString();
      endDate = customDateRange[1].endOf('day').valueOf().toString();
    }
    
    return { startDate, endDate };
  };

  const applyFilters = (currentStatus, currentDateFilter, currentCustomRange, currentSearch, currentSortOrder = dateSortOrder) => {
    if (onRefresh) {
      const now = moment();
      let startDate = '';
      let endDate = '';
      
      if (currentDateFilter === 'today') {
        startDate = now.startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (currentDateFilter === 'week') {
        startDate = now.clone().subtract(7, 'days').startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (currentDateFilter === 'custom' && currentCustomRange && currentCustomRange.length === 2) {
        startDate = currentCustomRange[0].startOf('day').valueOf().toString();
        endDate = currentCustomRange[1].endOf('day').valueOf().toString();
      }
      
      const filters = {
        status: currentStatus === 'all' ? '' : currentStatus,
        startDate,
        endDate,
        sortBy: 'createdAt',
        sortOrder: currentSortOrder
      };
      
      setPagination((prev) => ({ ...prev, current: 1 }));
      onRefresh(0, pagination.pageSize, currentSearch, filters);
    }
  };

  const handleRefresh = () => {
    applyFilters(statusFilter, dateFilter, customDateRange, searchTerm);
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setDateFilter('all');
    setCustomDateRange(null);
    setSearchTerm('');
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (onRefresh) {
      onRefresh(0, pagination.pageSize, '', {
        status: '',
        startDate: '',
        endDate: ''
      });
    }
  };

  const handleFilterChange = () => {
    applyFilters(statusFilter, dateFilter, customDateRange, searchTerm);
  };

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, total: totalCount }));
  }, [totalCount]);

  // Data is already filtered and sorted on the backend
  const paginatedFeedback = useMemo(() => {
    // Backend handles pagination, so we just use the data as-is
    return feedbackData || [];
  }, [feedbackData]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    if (onRefresh) {
      const now = moment();
      let startDate = '';
      let endDate = '';
      
      if (dateFilter === 'today') {
        startDate = now.startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (dateFilter === 'week') {
        startDate = now.clone().subtract(7, 'days').startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (dateFilter === 'custom' && customDateRange && customDateRange.length === 2) {
        startDate = customDateRange[0].startOf('day').valueOf().toString();
        endDate = customDateRange[1].endOf('day').valueOf().toString();
      }
      
      const filters = {
        status: statusFilter === 'all' ? '' : statusFilter,
        startDate,
        endDate
      };
      const offset = ((pageSize || pagination.pageSize) * (page - 1));
      onRefresh(offset, pageSize || pagination.pageSize, searchTerm, filters);
    }
  };

  const handlePageSizeChange = (current, size) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: size,
    }));
    if (onRefresh) {
      const now = moment();
      let startDate = '';
      let endDate = '';
      
      if (dateFilter === 'today') {
        startDate = now.startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (dateFilter === 'week') {
        startDate = now.clone().subtract(7, 'days').startOf('day').valueOf().toString();
        endDate = now.endOf('day').valueOf().toString();
      } else if (dateFilter === 'custom' && customDateRange && customDateRange.length === 2) {
        startDate = customDateRange[0].startOf('day').valueOf().toString();
        endDate = customDateRange[1].endOf('day').valueOf().toString();
      }
      
      const filters = {
        status: statusFilter === 'all' ? '' : statusFilter,
        startDate,
        endDate
      };
      onRefresh(0, size, searchTerm, filters);
    }
  };

  return (
    <SentryWrap>
    <NoPaddingWrapper>
      <TableContainer>
        <TableHeader>
          <HeaderTitle>User Reported Issues</HeaderTitle>
          <HeaderActions>
            <RefreshButton onClick={handleRefresh} title="Refresh" disabled={loading}>
              <Refresh style={{ color: 'white', fontSize: 20 }} />
            </RefreshButton>
            <StatusFilterWrapper>
              <FilterLabel>Status:</FilterLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  setStatusFilter(newStatus);
                  applyFilters(newStatus, dateFilter, customDateRange, searchTerm);
                }}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  minWidth: 150,
                  height: 32,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '& .MuiSelect-select': {
                    padding: 0,
                    lineHeight: '32px',
                    fontSize: '12px',
                    textAlign: 'center',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="Not started">Not started</MenuItem>
                <MenuItem value="In progress">In progress</MenuItem>
                <MenuItem value="Under review">Under review</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </StatusFilterWrapper>
            <StatusFilterWrapper>
              <FilterLabel>Date Range:</FilterLabel>
              <Select
                value={dateFilter}
                onChange={(e) => {
                  const newDateFilter = e.target.value;
                  setDateFilter(newDateFilter);
                  if (newDateFilter !== 'custom') {
                    setCustomDateRange(null);
                    applyFilters(statusFilter, newDateFilter, null, searchTerm);
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  minWidth: 150,
                  height: 32,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '& .MuiSelect-select': {
                    padding: 0,
                    lineHeight: '32px',
                    fontSize: '12px',
                    textAlign: 'center',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </StatusFilterWrapper>
            {dateFilter === 'custom' && (
              <StatusFilterWrapper>
                <RangePicker
                  value={customDateRange}
                  onChange={(dates) => {
                    setCustomDateRange(dates);
                    if (dates && dates.length === 2) {
                      applyFilters(statusFilter, 'custom', dates, searchTerm);
                    }
                  }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => {
                    // Disable future dates (only allow dates up to today)
                    return current && current > moment().endOf('day');
                  }}
                  style={{
                    height: '32px',
                    borderRadius: '20px',
                    backgroundColor: 'white',
                  }}
                />
              </StatusFilterWrapper>
            )}
            <StatusFilterWrapper>
              <FilterLabel>Sort:</FilterLabel>
              <Select
                value={dateSortOrder}
                onChange={(e) => {
                  const newSortOrder = e.target.value;
                  setDateSortOrder(newSortOrder);
                  applyFilters(statusFilter, dateFilter, customDateRange, searchTerm, newSortOrder);
                }}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  minWidth: 150,
                  height: 32,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '& .MuiSelect-select': {
                    padding: 0,
                    lineHeight: '32px',
                    fontSize: '12px',
                    textAlign: 'center',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
              </Select>
            </StatusFilterWrapper>
            <SearchInput
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  applyFilters(statusFilter, dateFilter, customDateRange, e.target.value);
                }
              }}
              onBlur={(e) => {
                applyFilters(statusFilter, dateFilter, customDateRange, e.target.value);
              }}
              variant="outlined"
              size="small"
              placeholder="Search by name, username, content, or role"
              InputProps={{
                sx: {
                  height: 32,
                  padding: '0 12px !important',
                  borderRadius: '20px',
                  backgroundColor: 'white',

                  '& .MuiOutlinedInput-input': {
                    padding: 0,
                    lineHeight: '28px',
                    fontSize: '12px',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
              }}
            />
            {hasActiveFilters && (
              <ResetButton onClick={handleResetFilters} title="Reset Filters" disabled={loading}>
                <Clear style={{ color: 'white', fontSize: 18, marginRight: 4 }} />
                <span style={{ fontSize: '12px' }}>Reset</span>
              </ResetButton>
            )}
          </HeaderActions>
        </TableHeader>
        
        <Table
          style={{
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#fff',
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader.map((item) => (
                <StyledTableHead key={item.id}>{item?.headerText}</StyledTableHead>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <div style={{ padding: '20px 0' }}>
                    <Loading size={30} />
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {isEmpty(paginatedFeedback) && (
                <TableRow key={'0'} style={{ border: 0 }}>
                  <TableCell colSpan={9} style={{ textAlign: 'center' }}>
                    No user reported issues available
                  </TableCell>
                </TableRow>
              )}

              {paginatedFeedback?.map((element, index) => (
                <FeedbackTableRow
                  key={element.id || `${element.name}-${index}`}
                  row={element}
                  sequenceNo={(pagination.current - 1) * pagination.pageSize + index + 1}
                  onUpdateItem={onUpdateItem}
                />
              ))}

              {!isEmpty(paginatedFeedback) && (
                <TableRow>
                  <TableCell colSpan={9} align="right">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handlePageChange}
                      showSizeChanger={true}
                      pageSizeOptions={pagination.pageSizeOptions}
                      onShowSizeChange={handlePageSizeChange}
                      showQuickJumper={true}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                      itemRender={(page, type, originalElement) => {
                        if (type === 'page' && page === pagination.current) {
                          return (
                            <div className="ant-pagination-item-active custom-active-page">
                              {page}
                            </div>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </NoPaddingWrapper>
    </SentryWrap>
  );
};

export default memo(FeedbackModuleComponent);

const NoPaddingWrapper = styled.div`
  margin: -24px;
  height: calc(100vh - 64px);
  overflow: auto;
`;

const TableContainer = styled.div`
  background: white;
`;

const TableHeader = styled.div`
  background: #1e3a8a;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatusFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
`;

const StyledTableHead = styled(TableCell)`
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 16px;
  background-color: #dbeafe;
  color: #374151;
`;

const SearchInput = styled(TextField)`
  background-color: white;
  min-width: 220px;
  height: 32px;
  border-radius: 20px;
  padding: 0 12px;
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
`;

const RefreshButton = styled(IconButton)`
  color: white !important;
  padding: 6px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ResetButton = styled(Button)`
  color: white !important;
  padding: 6px 12px;
  height: 32px;
  min-width: auto;
  text-transform: none;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

